/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  FLOW_FAILED_STATUSES,
  type FlowRunProgress,
  FlowRunState,
  type FlowRunStatus,
} from '@poweralpha/common';
import dayjs from 'dayjs';

/** Running runs never show more than this until Prefect reports a terminal state. */
export const MAX_RUNNING_FRACTION = 0.99;
/** The ETA only shows once a run is this far along; earlier rates are too noisy. */
const MIN_ETA_FRACTION = 0.1;

export const QUEUED_STATES: ReadonlyArray<FlowRunState> = [
  FlowRunState.SCHEDULED,
  FlowRunState.PENDING,
];

/** Started and not yet finished; cancelling and paused runs have not reached a terminal state. */
export const IN_PROGRESS_STATES: ReadonlyArray<FlowRunState> = [
  FlowRunState.RUNNING,
  FlowRunState.CANCELLING,
  FlowRunState.PAUSED,
];

export const isRunInProgress = (run: FlowRunStatus) => IN_PROGRESS_STATES.includes(run.state.type);

export const isRunActive = (run: FlowRunStatus) =>
  isRunInProgress(run) || QUEUED_STATES.includes(run.state.type);

export const isRunFailed = (run: FlowRunStatus) => FLOW_FAILED_STATUSES.includes(run.state.type);

/** Processed share of a running run (failed units count as processed), or null without an estimate. */
export const getRunFraction = (progress: FlowRunProgress | undefined): number | null =>
  progress?.total
    ? Math.min(MAX_RUNNING_FRACTION, (progress.completed + progress.failed) / progress.total)
    : null;

export interface WeightedFraction {
  fraction: number | null;
  weight: number;
}

/** Weighted mean of the runs that have an estimate; a batch weighs its number of cases. */
export const getOverallFraction = (runs: ReadonlyArray<WeightedFraction>): number | null => {
  const known = runs.filter(
    (run): run is { fraction: number; weight: number } => run.fraction !== null,
  );
  const totalWeight = known.reduce((total, run) => total + run.weight, 0);
  if (!totalWeight) return null;
  return known.reduce((total, run) => total + run.fraction * run.weight, 0) / totalWeight;
};

const formatMinutes = (ms: number) => {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const getElapsedMs = (startTime: string, now: number) =>
  Math.max(0, now - dayjs(startTime).valueOf());

/** Time left if the rate so far holds; null early in a run or without an estimate. */
const getRemainingMs = (
  startTime: string | null | undefined,
  fraction: number | null,
  now: number,
) =>
  startTime && fraction !== null && fraction >= MIN_ETA_FRACTION
    ? (getElapsedMs(startTime, now) * (1 - fraction)) / fraction
    : null;

/** `8m elapsed · ~3m left` */
export const formatRunTiming = (
  startTime: string | null | undefined,
  fraction: number | null,
  now = Date.now(),
) => {
  if (!startTime) return '';
  const remaining = getRemainingMs(startTime, fraction, now);
  return [
    `${formatMinutes(getElapsedMs(startTime, now))} elapsed`,
    remaining === null ? null : `~${formatMinutes(remaining)} left`,
  ]
    .filter(Boolean)
    .join(' · ');
};

/** `~23m until all done`, from the slowest running run; null while any run has no estimate. */
export const formatAllDone = (
  runs: ReadonlyArray<{ startTime: string | null | undefined; fraction: number | null }>,
  now = Date.now(),
) => {
  const remaining = runs.map((run) => getRemainingMs(run.startTime, run.fraction, now));
  if (!remaining.length || remaining.includes(null)) return null;
  return `~${formatMinutes(Math.max(...(remaining as Array<number>)))} until all done`;
};

/** `17 / 24 tasks`, `56 done · 2 failed · of 120 cases` */
export const formatRunCounts = (progress: FlowRunProgress | undefined) => {
  if (!progress?.total) return null;
  const noun = progress.kind === 'cases' ? 'cases' : 'tasks';
  if (!progress.failed) return `${progress.completed} / ${progress.total} ${noun}`;
  return `${progress.completed} done · ${progress.failed} failed · of ${progress.total} ${noun}`;
};

export type SimProgressPhase = 'hidden' | 'starting' | 'running' | 'done' | 'failed' | 'cancelled';

/** Phases in which every tracked run has reached a terminal state. */
export const TERMINAL_PHASES: ReadonlyArray<SimProgressPhase> = ['done', 'failed', 'cancelled'];

/**
 * What the top-bar pill shows for the runs tracked this session: progress while any is active,
 * then how they ended. A failure wins over completions, and completions over cancellations.
 */
export const getPillPhase = (
  trackedRuns: ReadonlyArray<FlowRunStatus>,
  overall: number | null,
): SimProgressPhase => {
  if (!trackedRuns.length) return 'hidden';
  if (trackedRuns.some(isRunActive)) {
    // Runs that are only winding down (cancelling, paused) have nothing left to estimate.
    const isWindingDown = !trackedRuns.some(
      (run) => run.state.type === FlowRunState.RUNNING || QUEUED_STATES.includes(run.state.type),
    );
    return overall !== null || isWindingDown ? 'running' : 'starting';
  }
  if (trackedRuns.some(isRunFailed)) return 'failed';
  if (trackedRuns.some((run) => run.state.type === FlowRunState.COMPLETED)) return 'done';
  return 'cancelled';
};

export interface PillCounts {
  running: number;
  done: number;
  failed: number;
  cancelled: number;
}

const joinCounts = (...parts: Array<string | false>) => parts.filter(Boolean).join(' · ');

/** `3 running`, `Starting…`, `3 done · 1 cancelled`, `1 failed · 2 done · 1 cancelled` */
export const getPillLabel = (phase: SimProgressPhase, counts: PillCounts) => {
  const cancelled = counts.cancelled > 0 && `${counts.cancelled} cancelled`;
  switch (phase) {
    case 'running':
      return `${counts.running} running`;
    case 'done':
      return joinCounts(`${counts.done} done`, cancelled);
    case 'failed':
      return joinCounts(
        `${counts.failed} failed`,
        counts.done > 0 && `${counts.done} done`,
        cancelled,
      );
    case 'cancelled':
      return `${counts.cancelled} cancelled`;
    default:
      return 'Starting…';
  }
};

export const formatPercent = (fraction: number) => `${Math.round(fraction * 100)}%`;

/** First line of a Prefect state message, without the traceback. */
export const getFailureSummary = (run: FlowRunStatus) => {
  const firstLine = run.state.message
    ?.split('\n')
    .find((line) => line.trim())
    ?.trim();
  if (!firstLine) return `The run ended as ${run.state.name ?? run.state.type.toLowerCase()}.`;
  return firstLine.length > 200 ? `${firstLine.slice(0, 199)}…` : firstLine;
};
